import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Radio } from "@mui/material";
import { ContextProvider, translate } from "../../providers";
import { ESSENTIAL_TOURS } from "./tours";
import { isNonEmptyArray } from "../../utils";
import { setActiveTour, setContextTourExploration } from "../../slice";
import { GenericDialog, GenericDialogContent, GenericDialogFooter, GenericDialogHeader } from "../dialog";
import { CustomButton, CustomIconButton } from "../buttons";
import { icon, IconComponent } from "../icon-component";
import { getActivePlayableTours, isValidStoredDataFormat, parseData } from "./utils/utils";
import styles from "./ProductTourMenuDialog.module.css";

const PAGE_SIZE = 6;
export default function ProductTourMenuDialog({ open, onClose }) {
	const [currentPage, setCurrentPage] = useState(0);
	const [playedTours, setPlayedTours] = useState([]);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const reduxPlayableTours = useSelector(({ context }) => context.playableTours);
	const firstTimeGuiding = useSelector(({ context }) => context.firstTimeGuiding);
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const playableTours = useMemo(
		() => getActivePlayableTours(projectId, ESSENTIAL_TOURS, reduxPlayableTours),
		[projectId, reduxPlayableTours]
	);
	const paginatedTours = useMemo(() => {
		const start = currentPage * PAGE_SIZE;
		if (isNonEmptyArray(playableTours)) {
			return playableTours.slice(start, start + PAGE_SIZE);
		}
		return [];
	}, [playableTours, currentPage]);
	const lastPage = useMemo(
		() => (isNonEmptyArray(playableTours) && Math.ceil(playableTours.length / PAGE_SIZE) - 1) || 0,
		[playableTours]
	);
	const handleClickTour = (clickedTour) => {
		const { id, locations, redirection } = clickedTour;
		dispatch(setActiveTour(id));
		if (!matchPath({ path: locations[0] }, location.pathname) && redirection && typeof redirection === "function") {
			navigate(redirection(projectId));
		}
		onClose();
	};
	useEffect(() => {
		if (open) {
			const parsedTours = parseData(ContextProvider.getProductTours(), () => []);
			const isValidSavedPt = isValidStoredDataFormat(parsedTours);
			if (isValidSavedPt) {
				const pt = parsedTours?.[1];
				setPlayedTours(
					(isNonEmptyArray(pt) && [
						...new Set(
							pt.filter((savedPt) => playableTours.some((playablePt) => playablePt.id === savedPt))
						),
					]) ||
						[]
				);
			}
		}
	}, [open, playableTours]);
	const handleStart = () => {
		const unplayedTour = playableTours.find((playableTour) => !playedTours.some((pt) => pt === playableTour.id));
		if (unplayedTour) {
			handleClickTour(unplayedTour);
		}
	};
	const handleSkip = () => {
		dispatch(setContextTourExploration(true));
		onClose();
	};
	const handleOpenMenu = () => {
		dispatch(setContextTourExploration(false));
	};
	const handleGoBack = () => {
		if (currentPage > 0) {
			setCurrentPage((prev) => prev - 1);
		}
	};
	const handleGoNext = () => {
		if (currentPage < lastPage) {
			setCurrentPage((prev) => prev + 1);
		}
	};
	return (
		<GenericDialog className={styles.dialog} open={open} onClose={onClose}>
			{(!firstTimeGuiding && (
				<>
					<GenericDialogHeader className={styles.header}>
						<span className={styles.titleMain}>{translate("common:product-tour.dialog.title")}</span>
						<span className={styles.titleProgress}>
							{translate("common:product-tour.dialog.completion", {
								completedCount: playedTours.length,
								total: playableTours.length,
							})}
						</span>
					</GenericDialogHeader>
					<GenericDialogContent className={styles.content}>
						<div className={styles.tours}>
							{paginatedTours.map(({ key, ...tour }) => (
								<div
									data-played={playedTours.some((pt) => pt === tour.id)}
									role="presentation"
									onClick={() => handleClickTour(tour)}
								>
									{(playedTours.some((pt) => pt === tour.id) && (
										<IconComponent color="var(--blue-500)" icon={icon.faCheckCircle} size="xl" />
									)) || <Radio disabled checked={false} />}
									<div className={styles.tourName}>{translate(tour?.name)}</div>
									<div>
										<IconComponent
											color={
												(playedTours.some((pt) => pt === tour.id) && "var(--blue-500)") ||
												"var(--grey-500)"
											}
											icon={icon[tour?.icon]}
										/>
									</div>
								</div>
							))}
						</div>
						{playableTours.length > PAGE_SIZE && (
							<div className={styles.navigation}>
								<CustomIconButton
									disabled={currentPage === 0}
									icon={icon.faChevronLeft}
									iconColor={(!currentPage && "var(--grey-300)") || "var(--color-black)"}
									size="sm"
									onClick={handleGoBack}
								/>
								<CustomIconButton
									disabled={currentPage === lastPage}
									icon={icon.faChevronRight}
									iconColor={(currentPage === lastPage && "var(--grey-300)") || "var(--color-black)"}
									size="sm"
									onClick={handleGoNext}
								/>
							</div>
						)}
					</GenericDialogContent>
					<GenericDialogFooter className={styles.footer}>
						<CustomButton color="secondary" variant="outlined" onClick={onClose}>
							{translate("common:btn.close")}
						</CustomButton>
						<CustomButton
							color="primary"
							disabled={isNonEmptyArray(playedTours) && playedTours.length === playableTours.length}
							variant="contained"
							onClick={handleStart}
						>
							{translate((isNonEmptyArray(playedTours) && "common:btn.next") || "common:btn.start")}
						</CustomButton>
					</GenericDialogFooter>
				</>
			)) || (
				<>
					<GenericDialogContent className={styles.presentation}>
						<div>
							<div className={styles.introIcon}>
								<IconComponent color="var(--blue-500)" icon={icon.faFile} size="sm" />
							</div>
						</div>
						<div className={styles.introTitle}>
							<span>{translate("common:product-tour.dialog.welcome.title")}</span>
						</div>
						<div className={styles.introMessage}>
							<span>{translate("common:product-tour.dialog.welcome.message")}</span>
						</div>
					</GenericDialogContent>
					<GenericDialogFooter className={styles.footer}>
						<CustomButton color="secondary" variant="outlined" onClick={handleSkip}>
							{translate("common:product-tour.dialog.welcome.skip")}
						</CustomButton>
						<CustomButton color="primary" variant="contained" onClick={handleOpenMenu}>
							{translate("common:product-tour.dialog.welcome.start")}
						</CustomButton>
					</GenericDialogFooter>
				</>
			)}
		</GenericDialog>
	);
}
